import { useEffect, useState } from 'react';
import api from '../../API';
import { girderThumbnailUrl } from '../../utils/girderThumbnailUrl';
import styles from './CreateFolderModal.module.css';

/**
 * Edit organization branding (title + optional large-image logo item id).
 */
export default function OrganizationBrandingModal({
    open,
    folderId,
    folderName,
    initialTitle = '',
    initialLogoItemId = '',
    onClose,
    onSuccess,
}) {
    const [title, setTitle] = useState(initialTitle || '');
    const [logoItemId, setLogoItemId] = useState(initialLogoItemId || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [previewFailed, setPreviewFailed] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTitle(initialTitle || '');
        setLogoItemId(initialLogoItemId || '');
        setError('');
        setSaving(false);
        setPreviewFailed(false);
    }, [open, initialTitle, initialLogoItemId, folderId]);

    useEffect(() => {
        setPreviewFailed(false);
    }, [logoItemId]);

    if (!open) return null;

    const logoTrimmed = logoItemId.trim();
    const previewSrc = logoTrimmed && !previewFailed
        ? girderThumbnailUrl(api, logoTrimmed, { width: 96, height: 96 })
        : null;

    async function handleSave(e) {
        e.preventDefault();
        if (!folderId || saving) return;
        setSaving(true);
        setError('');
        try {
            const body = {
                title: title.trim(),
                logoItemId: logoTrimmed || null,
            };
            const r = await api.put(`projects/folders/${folderId}/branding`, { json: body });
            if (r.status !== 200) {
                setError(r.data?.message || 'Failed to save branding.');
                return;
            }
            onSuccess?.(r.data);
            onClose?.();
        } catch (err) {
            setError(err.message || err.response?.data?.message || 'Failed to save branding.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className={styles.modalOverlay}
            role="presentation"
            onClick={onClose}
        >
            <div
                className={styles.modalContent}
                role="dialog"
                aria-modal="true"
                aria-labelledby="org-branding-modal-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h3 id="org-branding-modal-title" className={styles.modalTitle}>
                        Branding{folderName ? `: ${folderName}` : ''}
                    </h3>
                    <button type="button" className={styles.closeButton} onClick={onClose} title="Close">
                        ×
                    </button>
                </div>
                <form onSubmit={handleSave}>
                    <div className={styles.modalBody}>
                        <p style={{ margin: 0, fontSize: '0.9em', color: '#6a737d' }}>
                            Shown in the top bar for projects under this folder. Use a large-image
                            item from your public assets collection for the logo (participants must
                            be able to read that item).
                        </p>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="org-branding-title">
                                Title
                            </label>
                            <input
                                id="org-branding-title"
                                className={styles.textInput}
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Leave blank to use default site title"
                                maxLength={200}
                                disabled={saving}
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="org-branding-logo">
                                Logo item ID
                            </label>
                            <input
                                id="org-branding-logo"
                                className={styles.textInput}
                                type="text"
                                value={logoItemId}
                                onChange={(e) => setLogoItemId(e.target.value)}
                                placeholder="Girder item id (optional)"
                                disabled={saving}
                            />
                        </div>
                        {previewSrc ? (
                            <div className={styles.fieldGroup}>
                                <span className={styles.fieldLabel}>Preview</span>
                                <img
                                    src={previewSrc}
                                    alt="Logo preview"
                                    style={{
                                        maxHeight: 48,
                                        maxWidth: '100%',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        alignSelf: 'center',
                                        borderRadius: 4,
                                    }}
                                    onError={() => setPreviewFailed(true)}
                                />
                            </div>
                        ) : null}
                        {error ? (
                            <div className={styles.errorMessage} role="alert">{error}</div>
                        ) : null}
                    </div>
                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
